/**
 * PolicenkontrolleBefundCard - the Policenkontrolle result + the concrete per-field difference, shared
 * by the Ausschreibung Hauptscreen (variant "banner"), the Tab-6 Abschluss section and the Posteingang
 * Bearbeitungsmaske Systemanalyse pane (variant "pane"). P2 hardening 05. Server owns every gate via
 * `aktionen`; this only renders + triggers. ASCII hyphens only (lint).
 *
 * [Source: apps/web/src/features/placements/AbschlussSection.tsx] (KontrolleCard + the Tab-6 idiom)
 */
import {
  PK_RUECKMELDUNG_ERNEUT_HINWEIS,
  PK_WARTET_AKTIVIERUNG_HINWEIS,
  type PolicenkontrolleEintrag,
} from '@brokly/shared';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { dateTimeShortDe } from '../../lib/format';
import { PolicenkontrolleTabelle } from './PolicenkontrolleTabelle';

export interface PolicenkontrolleBefundCardProps {
  kontrolle: PolicenkontrolleEintrag;
  placementNumber?: string;
  variant: 'banner' | 'pane';
  aktionen: { abweichung_klaeren: boolean; rueckmeldung_versicherer: boolean };
  canWrite: boolean;
  canSend: boolean;
  onKlaeren?: () => void;
  onRueckmeldung?: () => void;
  /** When set, an «Ausschreibung oeffnen» button is shown (the Maske pane links back to the screen). */
  onOpenAusschreibung?: () => void;
}

export function PolicenkontrolleBefundCard({
  kontrolle,
  placementNumber,
  variant,
  aktionen,
  canWrite,
  canSend,
  onKlaeren,
  onRueckmeldung,
  onOpenAusschreibung,
}: PolicenkontrolleBefundCardProps) {
  const { befund, result } = kontrolle;
  const abweichung = result === 'abweichung';
  const tone = abweichung ? 'border-status-danger/40 bg-status-danger/5' : 'border-status-success/40 bg-status-success/5';

  return (
    <div
      className={`rounded-lg border p-3 ${variant === 'banner' ? tone : 'border-border'}`}
      data-testid="pk-befund-card"
      data-result={result}
      data-variant={variant}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={abweichung ? 'danger' : 'success'} className="text-base font-bold">
          {abweichung ? 'Abweichung' : 'Deckungsgleich'}
        </Badge>
        <span className="text-sm font-semibold">{befund.headline}</span>
        {placementNumber && <span className="text-xs text-muted-foreground">· {placementNumber}</span>}
      </div>

      {befund.abweichungen.length > 0 && (
        <ul className="mt-2 space-y-1">
          {befund.abweichungen.map((line, i) => (
            <li key={i} className="text-sm text-status-danger" data-testid="pk-befund-abweichung">
              {line}
            </li>
          ))}
        </ul>
      )}
      {befund.nicht_pruefbar.length > 0 && (
        <ul className="mt-2 space-y-1">
          {befund.nicht_pruefbar.map((line, i) => (
            <li key={i} className="text-xs text-muted-foreground" data-testid="pk-befund-nicht-pruefbar">
              {line}
            </li>
          ))}
        </ul>
      )}

      {kontrolle.policy_status_bei_kontrolle === 'draft' && !abweichung && (
        <p className="mt-2 text-xs text-muted-foreground">{PK_WARTET_AKTIVIERUNG_HINWEIS}</p>
      )}

      {kontrolle.klaerung.status === 'geklaert' && (
        <p className="mt-2 text-xs text-muted-foreground">
          Geklaert{kontrolle.klaerung.geklaert_by_name ? ` von ${kontrolle.klaerung.geklaert_by_name}` : ''}
          {kontrolle.klaerung.grund ? `: ${kontrolle.klaerung.grund}` : ''}
        </p>
      )}

      {kontrolle.rueckmeldung && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="pk-befund-rueckmeldung">
          {PK_RUECKMELDUNG_ERNEUT_HINWEIS} - gesendet am {dateTimeShortDe(kontrolle.rueckmeldung.gesendet_at)} an{' '}
          {kontrolle.rueckmeldung.empfaenger}
          {kontrolle.rueckmeldung.gesendet_by_name ? ` (${kontrolle.rueckmeldung.gesendet_by_name})` : ''}
        </p>
      )}

      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-muted-foreground">Alle Felder</summary>
        <div className="mt-2">
          <PolicenkontrolleTabelle zeilen={kontrolle.zeilen} />
        </div>
      </details>

      {(aktionen.rueckmeldung_versicherer || aktionen.abweichung_klaeren || onOpenAusschreibung) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {aktionen.rueckmeldung_versicherer && onRueckmeldung && (
            <Button
              size="sm"
              variant="secondary"
              disabled={!canWrite || !canSend}
              title={!canSend ? 'Keine Berechtigung fuer den E-Mail-Versand' : undefined}
              onClick={onRueckmeldung}
              data-testid="pk-befund-rueckmeldung-btn"
            >
              Rueckmeldung an Versicherer
            </Button>
          )}
          {aktionen.abweichung_klaeren && onKlaeren && (
            <Button size="sm" variant="destructive" disabled={!canWrite} onClick={onKlaeren} data-testid="pk-befund-klaeren-btn">
              Abweichung geklaert
            </Button>
          )}
          {onOpenAusschreibung && (
            <Button size="sm" variant="ghost" onClick={onOpenAusschreibung} data-testid="pk-befund-open-ausschreibung">
              Ausschreibung oeffnen
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
